import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (attempts.current >= 8) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 8) { setState("error"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="max-w-md w-full p-10 text-center border-border" data-testid="payment-success-card">
        {state === "checking" && (<>
          <Loader2 className="h-12 w-12 mx-auto text-primary animate-spin mb-4" />
          <h1 className="font-heading text-2xl font-bold tracking-tight">Confirming payment…</h1>
          <p className="text-muted-foreground mt-2">Please wait a moment.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600 mb-4" />
          <h1 className="font-heading text-3xl font-bold tracking-tight">Payment successful</h1>
          <p className="text-muted-foreground mt-2">Your invoice has been marked as paid.</p>
          <Link to="/dashboard"><Button className="rounded-full mt-6 hover:bg-primary/90" data-testid="back-home-button">Back to Dashboard</Button></Link>
        </>)}
        {state === "pending" && (<>
          <Loader2 className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <h1 className="font-heading text-2xl font-bold tracking-tight">Payment processing</h1>
          <p className="text-muted-foreground mt-2">This is taking longer than usual. Your invoice will update shortly.</p>
          <Link to="/dashboard"><Button variant="outline" className="rounded-full mt-6">Back to Dashboard</Button></Link>
        </>)}
        {state === "error" && (<>
          <XCircle className="h-14 w-14 mx-auto text-destructive mb-4" />
          <h1 className="font-heading text-2xl font-bold tracking-tight">Something went wrong</h1>
          <p className="text-muted-foreground mt-2">We couldn't confirm this payment.</p>
          <Link to="/"><Button variant="outline" className="rounded-full mt-6">Back to Dashboard</Button></Link>
        </>)}
      </Card>
    </div>
  );
}
